// Inside a prime plot's building (room.ts draws the page at /plot/<n>): the
// same dollhouse cutaway as the town's rooms (roomsets.ts), sized to the
// building (a pavilion 4 × 3, a tea house and a studio 5 × 4, a little shop
// 4 × 4), floored and papered as its spec says, with what the owner placed
// inside (place sofa 1 1 inside), and each kind dressed as what it is: paper
// lanterns in the tea house, the owner's designs on the studio's walls, a
// counter and the window's stock in the little shop, bunting under the
// pavilion's open roof. Everything comes from GET /api/plot/<n>.
import * as THREE from 'three';
import { mat, sphere, at, rbox } from './kit.js';
import { wbox } from './house.js';
import * as F from './furniture.js';
import * as Fu from './furnish.js';
import { pixelTexture } from './pixel.js';
import { Board, type Built, type RoomSet, type Rect, type P } from './roomsets.js';

export type PlotData = {
  n: number; place: string; page: string; now: number; owner: string; owner_name: string; title: string;
  name?: string | null; about?: string | null; sign?: string | null; private?: boolean;
  where: string; size: [number, number]; out: [number, number, number, number];
  kind: string | null; kind_name: string | null; blurb: string | null; building: any;
  inside: Fu.Thing[]; yard: Fu.Thing[]; designs: Fu.Design[];
  stock: { n: number; words: string; price: number }[]; here: number;
};

const LOOKS: Record<string, { paper: RoomSet['paper']; wainscot: string; trim: string }> = {
  'tea-house': { paper: { bg: '#f3ead6', ink: '#d9c7a4', ink2: '#c0503f', kind: 'diamond' }, wainscot: '#8a5a3a', trim: '#5a3a26' },
  studio: { paper: { bg: '#f6f4ee', ink: '#e4e0d4', ink2: '#b9b2a2', kind: 'dots' }, wainscot: '#d8c4a2', trim: '#8a7a64' },
  shop: { paper: { bg: '#eef3f6', ink: '#cfe0ea', ink2: '#2f5e9e', kind: 'stripe' }, wainscot: '#2f5e9e', trim: '#1d3f6e' },
  pavilion: { paper: { bg: '#f4efe4', ink: '#e2d6bc', ink2: '#9bb87a', kind: 'sprig' }, wainscot: '#b99572', trim: '#6e4b33' },
};

// the building's own floor and walls, from its spec (the room is "pavilion" for every kind)
function roomOf(d: PlotData) {
  const r = d.building?.rooms?.[0] || {}, m = d.building?.masses?.[0] || {};
  return { W: Math.max(3, m.w || 4), D: Math.max(3, m.d || 3), floor: r.floor || { kind: 'planks', color: '#c9b48a' }, wall: r.wall?.color || '#efe9de' };
}

export function plotSet(d: PlotData): RoomSet {
  const { W, D, floor, wall } = roomOf(d);
  const kind = d.kind || 'pavilion', look = LOOKS[kind] || LOOKS.pavilion;
  return {
    W, D, wall: 2.9, door: [W / 2 - 0.6, W / 2 + 0.6],
    plaster: wall, paper: look.paper, floor: { kind: floor.kind, color: floor.color, board: 0.16 },
    wainscot: look.wainscot, trim: look.trim, island: '#c9c2ae',
    windows: D >= 4 ? [D / 2] : [],
    build: (g) => dress(g, d, W, D, kind),
  };
}

function dress(g: THREE.Group, d: PlotData, W: number, D: number, kind: string): Built {
  // what the owner put inside, standing on the boards (as at home: furnish.ts)
  const h = { version: 0, plot: [W, D], spec: { floorH: 0.04, storyH: 2.75, masses: [{ id: 'pavilion', x: 0, y: 0, w: W, d: D }] },
    furniture: d.inside, yard: [], designs: d.designs, chores: {}, resident: { name: d.owner_name, color: '#fff', glow: '#fff' } } as unknown as Fu.House;
  g.add(Fu.furniture(h));
  const avoid: Rect[] = d.inside.map((t) => ({ x: t.at[0] - 0.5, y: t.at[1] - 0.45, w: 1.0, d: 0.9 }));
  const lights: THREE.Light[] = [];
  const warm = (c: string, i: number, x: number, y: number, z: number) => { const l = new THREE.PointLight(c, i, 7, 1.7); l.position.set(x, y, z); lights.push(l); };
  let update: Built['update'];
  if (kind === 'tea-house') {
    // paper lanterns along the ceiling, and a tea shelf on the back wall with the kettle on it
    const cols = ['#ffb0c4', '#ffd36b', '#fff1c8'];
    for (let i = 0; i < Math.max(3, Math.round(W)); i++) {
      const c = cols[i % 3], l = sphere(0.16, mat(c, { emissive: c, emissiveIntensity: 1.5, rough: 0.8 }), 12, 10);
      l.scale.y = 1.3; l.position.set(0.6 + i * ((W - 1.2) / Math.max(1, Math.round(W) - 1)), 2.35, 1.0 + (i % 2) * 0.5); l.userData.noShadow = true; g.add(l);
    }
    const shelf = F.sideboard({ wood: 'walnut', w: Math.min(1.4, W - 1.6) } as any); shelf.position.set(W - 1.0, 0, 0.3); g.add(shelf);
    const kettle = new THREE.Group(), iron = mat('#3a3633', { rough: 0.5, metal: 0.4 });
    kettle.add(sphere(0.13, iron, 14, 10).translateY(0.12), rbox(0.03, 0.12, 0.03, 0.01, iron, 1).translateY(0.28));
    kettle.position.set(W - 1.2, 0.82, 0.3); g.add(kettle);
    for (let i = 0; i < 3; i++) g.add(at(rbox(0.07, 0.06, 0.07, 0.02, mat(['#f6efe0', '#c9d8c4', '#e8c9b0'][i], { rough: 0.6 }), 2), W - 0.8 + i * 0.12, 0.82, 0.35));
    avoid.push({ x: W - 1.8, y: 0, w: 1.6, d: 0.7 });
    warm('#ffcf9a', 5, W / 2, 2.2, D / 2);
  } else if (kind === 'studio') {
    // the owner's designs, framed along the back wall (the newest first), and a north light
    const frame = mat('#3a2e26', { rough: 0.6 });
    const shown = d.designs.slice(0, Math.max(1, Math.floor((W - 0.6) / 1.3)));
    shown.forEach((ds, i) => {
      const pic = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.8), mat('#ffffff', { map: pixelTexture(ds.rows, ds.palette), rough: 0.9 }));
      const x = 0.9 + i * 1.3;
      g.add(wbox(x - 0.56, 1.24, 0.02, x + 0.56, 2.16, 0.07, frame)); pic.position.set(x, 1.7, 0.08); g.add(pic);
    });
    if (!shown.length) { const easel = F.painting({ w: 0.9, h: 0.7, seed: d.n } as any); easel.position.set(W / 2, 1.7, 0.06); g.add(easel); }
    warm('#e9f2ff', 5, W / 2, 2.4, 1.0);
  } else if (kind === 'shop') {
    // the counter along the back, and the window's stock chalked on a board above it
    // a shop counter: a blue front, a wooden top, a brass bell and a jar of sweets on it
    const cl = Math.min(2.4, W - 1.2), x0 = W / 2 - cl / 2;
    g.add(wbox(x0, 0.04, 0.45, x0 + cl, 0.95, 1.05, mat('#2f5e9e', { rough: 0.6 })));
    g.add(wbox(x0 - 0.05, 0.95, 0.4, x0 + cl + 0.05, 1.01, 1.1, mat('#b98a5a', { rough: 0.5 })));
    g.add(at(sphere(0.07, mat('#d6b05a', { rough: 0.3, metal: 0.6 }), 12, 8), x0 + 0.3, 1.06, 0.8));
    const jar = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.24, 14), mat('#dfeef2', { rough: 0.1, transparent: true, opacity: 0.55 } as any));
    jar.position.set(x0 + cl - 0.35, 1.13, 0.75); g.add(jar);
    for (let i = 0; i < 5; i++) g.add(at(sphere(0.035, mat(['#e86a92', '#f2c14e', '#5fb3d9', '#8fcf6a', '#b98ae8'][i]), 8, 6), x0 + cl - 0.4 + (i % 3) * 0.04, 1.05 + Math.floor(i / 3) * 0.05, 0.73 + (i % 2) * 0.04));
    const board = new Board(Math.min(2.6, W - 0.6), 0.9, [520, 180], '#24332c');
    board.mesh.position.set(W / 2, 2.1, 0.04); g.add(board.mesh);
    update = (x: PlotData) => board.set([{ text: x.name || `${x.owner_name}'s shop`, size: 30, color: '#f2c14e' },
      ...(x.stock.length ? x.stock.slice(0, 3).map((s) => ({ text: `${s.words} · ${s.price}`, size: 26, color: '#f6f2e8' })) : [{ text: 'the window is empty just now', size: 24, color: '#cfd8d2' }])]);
    update(d);
    avoid.push({ x: W / 2 - 1.3, y: 0, w: 2.6, d: 1.2 });
    warm('#fff0d0', 5, W / 2, 2.4, D / 2);
  } else {
    // the pavilion: bunting under the roof, and a bench along the back
    const cols = ['#e86a92', '#f2c14e', '#5fb3d9', '#8fcf6a'];
    for (let i = 0; i < Math.round(W * 3); i++) {
      const f = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.2, 3), mat(cols[i % 4], { rough: 0.8 }));
      const x = 0.2 + i * ((W - 0.4) / Math.max(1, Math.round(W * 3) - 1));
      f.rotation.x = Math.PI; f.position.set(x, 2.45 - Math.sin(Math.PI * x / W) * 0.25, D / 2); f.userData.noShadow = true; g.add(f);
    }
    const b = F.bench({} as any); b.position.set(W / 2, 0, 0.45); g.add(b);
    avoid.push({ x: W / 2 - 0.8, y: 0, w: 1.6, d: 0.7 });
    warm('#ffe2b0', 4, W / 2, 2.2, D / 2);
  }
  const starts: P[] = [[W / 2 - 0.8, D - 0.9], [W / 2 + 0.8, D - 0.9], [W / 2, D - 1.6], [0.9, D - 1.2], [W - 0.9, D - 1.2]];
  return { avoid, starts, lights, update: update as any };
}
